import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
    type PaginationState,
} from '@tanstack/react-table';
import { ChevronLeft, ChevronRight, Table as TableIcon } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { useTreasuryGrid } from '../api/treasuryApi';
import type { TreasuryGridRow } from '../types';
import { cn, formatBps, formatDate, formatYield } from '../utils/formatters';

// Regime values emitted by the backend math engine (market_regime column).
// NOTE: these must match the strings persisted in treasury_analytics exactly
// or the server-side .in_() filter returns nothing. "Normal"/"Inverted" are
// curve_shape values, NOT regimes, so they are intentionally excluded here.
const REGIME_OPTIONS = [
    'Bull Steepener',
    'Bear Steepener',
    'Bull Flattener',
    'Bear Flattener',
    'Neutral',
];

// 1. Column Definitions using TanStack's ColumnHelper for strict TypeScript inference
const columnHelper = createColumnHelper<TreasuryGridRow>();

export const TreasuryGrid: React.FC = () => {
    // --- Server-Side Pagination State ---
    const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 15, // Show 15 trading days per page
    });

    const pagination = useMemo(
        () => ({
            pageIndex,
            pageSize,
        }),
        [pageIndex, pageSize],
    );

    // --- Backend Filter State ---
    const [selectedRegimes, setSelectedRegimes] = useState<string[]>([]);
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');

    // Fetch paginated + filtered data via our React Query hook.
    // We join the regimes array into a comma-separated string to match the
    // FastAPI dependency, and pass undefined for empty filters so Axios drops
    // the params entirely.
    const offset = pageIndex * pageSize;
    const { data, isLoading, isFetching, error } = useTreasuryGrid(
        pageSize,
        offset,
        selectedRegimes.length > 0 ? selectedRegimes.join(',') : undefined,
        fromDate !== '' ? fromDate : undefined,
        toDate !== '' ? toDate : undefined,
    );

    // Whenever a filter changes we must jump back to page 0, otherwise the user
    // can get stranded on a now-empty page (e.g. page 12 of an unfiltered set).
    const resetToFirstPage = () =>
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));

    const toggleRegime = (regime: string) => {
        resetToFirstPage();
        setSelectedRegimes((prev) =>
            prev.includes(regime)
                ? prev.filter((r) => r !== regime)
                : [...prev, regime],
        );
    };

    const handleDateChange = (type: 'from' | 'to', val: string) => {
        resetToFirstPage();
        if (type === 'from') setFromDate(val);
        else setToDate(val);
    };

    const handleClearFilters = () => {
        resetToFirstPage();
        setSelectedRegimes([]);
        setFromDate('');
        setToDate('');
    };

    const hasActiveFilters =
        selectedRegimes.length > 0 || fromDate !== '' || toDate !== '';

    // --- Strict Column Definitions ---
    const columns = useMemo(
        () => [
            columnHelper.accessor('date', {
                header: 'Date',
                cell: (info) => (
                    <span className="font-semibold text-slate-900 dark:text-slate-100 whitespace-nowrap">
                        {formatDate(info.getValue())}
                    </span>
                ),
            }),
            columnHelper.accessor('market_regime', {
                header: 'Regime',
                cell: (info) => {
                    const val = info.getValue();
                    if (!val) return <span className="text-slate-400">--</span>;
                    const isBull = val.includes('Bull');
                    const isBear = val.includes('Bear');
                    return (
                        <span
                            className={cn(
                                'px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap border',
                                isBull
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800'
                                    : isBear
                                      ? 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800'
                                      : 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
                            )}
                        >
                            {val}
                        </span>
                    );
                },
            }),
            columnHelper.accessor('spread_10y_2y_bps', {
                header: '10Y-2Y',
                cell: (info) => {
                    const val = info.getValue();
                    if (val == null) return <span className="text-slate-400">--</span>;
                    return (
                        <span
                            className={cn(
                                'font-mono font-medium',
                                val < 0
                                    ? 'text-rose-600 dark:text-rose-400'
                                    : 'text-emerald-600 dark:text-emerald-400',
                            )}
                        >
                            {formatBps(val)}
                        </span>
                    );
                },
            }),
            // Core Tenors (Yields)
            columnHelper.accessor('month1', {
                header: '1M',
                cell: (i) => (
                    <span className="font-mono text-slate-500 dark:text-slate-400">
                        {formatYield(i.getValue())}
                    </span>
                ),
            }),
            columnHelper.accessor('month3', {
                header: '3M',
                cell: (i) => (
                    <span className="font-mono text-slate-500 dark:text-slate-400">
                        {formatYield(i.getValue())}
                    </span>
                ),
            }),
            columnHelper.accessor('month6', {
                header: '6M',
                cell: (i) => (
                    <span className="font-mono text-slate-500 dark:text-slate-400">
                        {formatYield(i.getValue())}
                    </span>
                ),
            }),
            columnHelper.accessor('year1', {
                header: '1Y',
                cell: (i) => (
                    <span className="font-mono text-slate-500 dark:text-slate-400">
                        {formatYield(i.getValue())}
                    </span>
                ),
            }),
            columnHelper.accessor('year2', {
                header: '2Y',
                cell: (i) => (
                    <span className="font-mono font-bold text-slate-700 dark:text-slate-300">
                        {formatYield(i.getValue())}
                    </span>
                ),
            }),
            columnHelper.accessor('year5', {
                header: '5Y',
                cell: (i) => (
                    <span className="font-mono text-slate-500 dark:text-slate-400">
                        {formatYield(i.getValue())}
                    </span>
                ),
            }),
            columnHelper.accessor('year10', {
                header: '10Y',
                cell: (i) => (
                    <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400">
                        {formatYield(i.getValue())}
                    </span>
                ),
            }),
            columnHelper.accessor('year30', {
                header: '30Y',
                cell: (i) => (
                    <span className="font-mono text-slate-500 dark:text-slate-400">
                        {formatYield(i.getValue())}
                    </span>
                ),
            }),
            columnHelper.accessor('volatility_10y_63d', {
                header: '63D Vol',
                cell: (info) => {
                    const val = info.getValue();
                    if (val == null) return <span className="text-slate-400">--</span>;
                    return (
                        <span className="font-mono text-indigo-600 dark:text-indigo-400">
                            {(val * 100).toFixed(1)} bps
                        </span>
                    );
                },
            }),
        ],
        [],
    );

    const tableData = useMemo(() => data?.data ?? [], [data]);
    const totalCount = data?.total_count ?? 0;
    const pageCount = Math.ceil(totalCount / pageSize);

    // --- TanStack Table Instance ---
    const table = useReactTable({
        data: tableData,
        columns,
        pageCount: pageCount > 0 ? pageCount : -1,
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true, // Crucial: Tells TanStack we are handling pagination on the server/API via React Query
    });

    return (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm flex flex-col mt-6 overflow-hidden relative">
            {/* Loading Overlay for Pagination Transitions */}
            {isFetching && !isLoading && (
                <div className="absolute inset-0 bg-white/40 dark:bg-slate-900/40 backdrop-blur-[1px] z-10 flex items-center justify-center transition-all duration-200">
                    <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                </div>
            )}

            {/* Header + Filter Controls */}
            <div className="p-5 md:p-6 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 text-slate-600 dark:text-slate-400 rounded-lg shadow-sm">
                            <TableIcon className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 tracking-tight">
                                Historical Data Matrix
                            </h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                Raw term structure and derived analytics (
                                {totalCount.toLocaleString()} records)
                            </p>
                        </div>
                    </div>

                    {hasActiveFilters && (
                        <button
                            onClick={handleClearFilters}
                            className="text-xs font-semibold text-rose-500 hover:text-rose-700 dark:hover:text-rose-400 whitespace-nowrap"
                        >
                            Reset Filters
                        </button>
                    )}
                </div>

                <div className="flex flex-col xl:flex-row xl:items-end gap-6 mt-5">
                    {/* Regime chips */}
                    <div className="flex-1">
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                            Filter by Market Regime
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {REGIME_OPTIONS.map((regime) => (
                                <button
                                    key={regime}
                                    onClick={() => toggleRegime(regime)}
                                    className={cn(
                                        'px-3 py-1 text-xs font-bold rounded-full border transition-colors',
                                        selectedRegimes.includes(regime)
                                            ? 'bg-slate-800 border-slate-800 text-white dark:bg-slate-100 dark:border-slate-100 dark:text-slate-900 shadow-sm'
                                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400',
                                    )}
                                >
                                    {regime}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Date range */}
                    <div className="flex gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                                From Date
                            </label>
                            <input
                                type="date"
                                value={fromDate}
                                max={toDate || undefined}
                                onChange={(e) => handleDateChange('from', e.target.value)}
                                className="bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                                To Date
                            </label>
                            <input
                                type="date"
                                value={toDate}
                                min={fromDate || undefined}
                                onChange={(e) => handleDateChange('to', e.target.value)}
                                className="bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Container (Horizontal Scroll for Mobile) */}
            <div className="overflow-x-auto min-h-[400px]">
                <table className="w-full text-sm text-left">
                    <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        className="px-6 py-4 font-bold tracking-wider whitespace-nowrap"
                                    >
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {isLoading ? (
                            // Initial Loading Skeleton Rows
                            Array.from({ length: pageSize }).map((_, idx) => (
                                <tr key={idx} className="animate-pulse">
                                    {columns.map((_, colIdx) => (
                                        <td key={colIdx} className="px-6 py-4">
                                            <div className="h-4 bg-slate-200 dark:bg-slate-700/50 rounded w-full"></div>
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : error ? (
                            // Error State
                            <tr>
                                <td
                                    colSpan={columns.length}
                                    className="px-6 py-12 text-center text-rose-500 font-medium"
                                >
                                    Failed to load grid data.
                                </td>
                            </tr>
                        ) : table.getRowModel().rows.length === 0 ? (
                            // Empty State
                            <tr>
                                <td
                                    colSpan={columns.length}
                                    className="px-6 py-12 text-center text-slate-500"
                                >
                                    {hasActiveFilters
                                        ? 'No data matches the selected filters.'
                                        : 'No data found.'}
                                </td>
                            </tr>
                        ) : (
                            // Render Actual Rows
                            table.getRowModel().rows.map((row) => (
                                <tr
                                    key={row.id}
                                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id} className="px-6 py-3.5">
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext(),
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-slate-500 dark:text-slate-400">
                    Showing{' '}
                    <span className="font-bold text-slate-700 dark:text-slate-200">
                        {totalCount > 0 ? pageIndex * pageSize + 1 : 0}
                    </span>{' '}
                    to{' '}
                    <span className="font-bold text-slate-700 dark:text-slate-200">
                        {Math.min((pageIndex + 1) * pageSize, totalCount)}
                    </span>{' '}
                    of{' '}
                    <span className="font-bold text-slate-700 dark:text-slate-200">
                        {totalCount.toLocaleString()}
                    </span>{' '}
                    rows
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                        className="p-2 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>

                    <span className="text-sm font-medium text-slate-600 dark:text-slate-300 px-2">
                        Page {table.getState().pagination.pageIndex + 1} of{' '}
                        {table.getPageCount() > 0 ? table.getPageCount() : 1}
                    </span>

                    <button
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                        className="p-2 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TreasuryGrid;
